import { useGetAgendaViewRecordsQuery } from 'store/services/flow/agendaRecords';
import { useGetUsableViewQuery } from 'store/services/flow/views';
import { memo, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { InputLabel, styled } from '@mui/material';
import { toast } from 'react-toastify';

import { Button } from 'components/mui/common/Button';
import { NameArray } from 'components/common/Table/cellComponents/NameArray';
import { Loader } from 'components/common/Loader';
import { useModals, useValueFormItems } from 'utils/hooks';

import { getValuesByFormItemId } from 'utils/agendaRecord/agendaRecord.ts';
import { isDisplayValue } from 'types/agendaRecord';
import { RecordsFilterModal } from './RecordsFilterModal';
import type { AgendaRecordItem, GetAgendaRecordsRes, RecordsFormItem } from 'types';
import { useFormContext, useWatch } from 'react-hook-form';
import { isNormalizedValueChanged } from 'utils/common';

const RecordsFilterWrapper = styled('div')(() => ({
  width: '100%',
}));

interface RecordsFilterProps {
  value?: string | string[];
  onChange: (value: string | string[] | null) => void;
  onBlur: () => void;
  disabled?: boolean;
  label: string;
  recordDefinition?: RecordsFormItem;
}

export const RecordsFilter = memo(({ value, onChange, onBlur, disabled, label, recordDefinition }: RecordsFilterProps) => {
  const { t } = useTranslation();

  const agendaId = recordDefinition?.agendaId ?? '';
  const viewId = recordDefinition?.viewId ?? '';
  const formItemIdToShow = recordDefinition?.formItemIdToShow ?? undefined;
  const paramsProp = recordDefinition?.params;

  const [modals, openModal, closeModal] = useModals({
    records_filter: false,
  });

  const { control } = useFormContext();

  const watchedValue = useWatch({
    control,
    name: (paramsProp ?? []).map((param) => param.valueFormItemId as string),
  });

  const isInitialized = useRef(false);
  const oldWatchedValue = useRef(watchedValue);

  useEffect(() => {
    if (watchedValue.some((v) => v === undefined)) {
      return;
    }

    if (!isInitialized.current) {
      isInitialized.current = true;
      oldWatchedValue.current = watchedValue;
      return;
    }

    const hasChanged = watchedValue.map((v, index) => isNormalizedValueChanged(v, oldWatchedValue.current[index])).some(Boolean);

    if (hasChanged) {
      oldWatchedValue.current = watchedValue;
      onChange(null);
    }
  }, [watchedValue, onChange]);

  const { data: { viewParameters = [] } = {} } = useGetUsableViewQuery(
    {
      parentId: agendaId,
      viewId: viewId,
      viewParentType: 'Agenda',
    },
    { skip: !viewId || !agendaId }
  );

  const params = useMemo(() => {
    if (!paramsProp) return [];

    return paramsProp.map((param, index) => {
      const valueDirect = watchedValue[index];

      if (valueDirect !== undefined && valueDirect !== null && valueDirect !== '') {
        return {
          ...param,
          valueDirect,
          valueSourceType: undefined,
          valueFormItemId: undefined,
        };
      }

      return param;
    });
  }, [paramsProp, watchedValue]);

  const valueFormItems = useValueFormItems({ viewParameters, params });

  const selectedRecordIds = useMemo(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  const agendaViewRecordParams = useMemo(() => {
    return {
      params: {
        agendaId,
        viewId,
        page: 1,
        pageSize: null,
        search: '',
        orderBy: '',
        formItemId: '',
        orderDesc: false,
      },
      evaluateVisualAttributes: false,
      skipViewConditions: false,
      recordIds: [],
      valueFormItems,
      filters: [],
    };
  }, [agendaId, valueFormItems, viewId]);

  const { data = {} as GetAgendaRecordsRes<AgendaRecordItem[]>, isFetching } = useGetAgendaViewRecordsQuery(agendaViewRecordParams, {
    refetchOnMountOrArgChange: true,
    skip: !viewId || !agendaId,
  });

  const selectedRecordsWithNames = useMemo(() => {
    if (!selectedRecordIds.length || !data.items || !formItemIdToShow) {
      return selectedRecordIds;
    }
    try {
      const displayValues = getValuesByFormItemId(data.items, formItemIdToShow, true);

      return selectedRecordIds.map((id) => {
        const foundItem = displayValues.find((item) => isDisplayValue(item) && item.id === id);
        if (foundItem && isDisplayValue(foundItem)) {
          return Array.isArray(foundItem.value) ? foundItem.value.toString() : foundItem.value;
        }
        return id;
      });
    } catch (error) {
      toast.warning(t('toast.error.action'));
      return selectedRecordIds;
    }
  }, [selectedRecordIds, data.items, formItemIdToShow, t]);

  const handleClose = () => {
    closeModal('records_filter')();
  };

  const handleSubmit = (newSelectedRecordIds: string[]) => {
    if (newSelectedRecordIds.length === 0) {
      onChange(null);
    } else {
      onChange(newSelectedRecordIds);
    }
    onBlur();
    closeModal('records_filter')();
  };

  const handleEdit = () => {
    openModal('records_filter')();
  };

  if (isFetching)
    return (
      <RecordsFilterWrapper>
        <InputLabel>{label}</InputLabel>
        <Loader />
      </RecordsFilterWrapper>
    );

  return (
    <>
      <RecordsFilterWrapper>
        <InputLabel>{label}</InputLabel>
        <Button type="button" variant="outlined" onClick={openModal('records_filter')} disabled={disabled} fullWidth>
          {selectedRecordIds.length === 0 ? t('common.select') : <NameArray value={selectedRecordsWithNames} onEdit={handleEdit} />}
        </Button>
      </RecordsFilterWrapper>

      <RecordsFilterModal
        title={label}
        open={modals?.records_filter}
        agendaId={agendaId}
        viewId={viewId}
        selectedRecordIds={selectedRecordIds}
        onClose={handleClose}
        onSubmit={handleSubmit}
        records={data.items || []}
      />
    </>
  );
});
